import { ETBundle, ETItem, ETLane } from './types';
import { LANE_GAP, TIGHT_ROW_PITCH } from './zoom';

export type LaneLayout = {
  laneId: string;
  top: number;
  rows: number;
  height: number;
  collapsed: boolean;
  collapsible: boolean;
};

export type ItemLayout = {
  itemId: string;
  row: number;
  y: number;
  hidden: boolean;
};

export type ClusterBadge = {
  key: string;
  laneId: string;
  anchorItemId: string;
  count: number;
};

export type BundleSegment = {
  bundleParentId: string;
  childId: string;
};

export type TimelineLayoutResult = {
  lanes: LaneLayout[];
  laneById: Record<string, LaneLayout>;
  items: Record<string, ItemLayout>;
  badges: ClusterBadge[];
  bundleSegments: BundleSegment[];
  hiddenIds: Set<string>;
  totalHeight: number;
};

export type ComputeLayoutInput = {
  lanes: ETLane[];
  items: ETItem[];
  bundles?: ETBundle[];
  colWidth: number;
  rowHeight: number;
  rowGap: number;
  collapsedLanes?: Record<string, boolean>;
  expandedClusters?: Record<string, boolean>;
  measureLabelDays?: (label: string, colWidth: number) => number;
};

// [start, end] day ranges, inclusive on both ends — two items overlap if
// they share at least one day.
const areOverlapping = (a: [number, number], b: [number, number]) =>
  a[0] <= b[1] && b[0] <= a[1];

// How many extra days a chip's inline label needs reserved so two adjacent
// items' labels don't overlap at Week/Detail zoom. Capped at 6 days —
// production labels are interpolated (`{{participant.fullName}}`) and can
// run much longer than the design prototype's short filler copy, so an
// uncapped reservation would blow the packer apart on a single long label.
export const defaultMeasureLabelDays = (label: string, colWidth: number) => {
  if (!label || colWidth <= 0) return 0;
  const days = Math.ceil((label.length * 6.2 + 26) / colWidth);
  return Math.max(0, Math.min(6, days));
};

type AtomicUnit = {
  si: number;
  ei: number;
  height: number;
  itemIds: string[]; // itemIds[0] is the anchor (standalone item, or bundle parent)
  bundle?: { parentId: string; childIds: string[] };
};

const buildLaneUnits = (
  laneItems: ETItem[],
  bundles: ETBundle[],
  measureLabelDays: (label: string, colWidth: number) => number,
  colWidth: number
): AtomicUnit[] => {
  const byId = new Map(laneItems.map((it) => [it.id, it]));
  const reachOf = (it: ETItem) =>
    Math.max(it.endDay, it.startDay + measureLabelDays(it.label, colWidth));

  const childIdsInBundle = new Set<string>();
  const parentIdsWithChildren = new Set<string>();
  const units: AtomicUnit[] = [];

  bundles.forEach((b) => {
    const parent = byId.get(b.parentId);
    if (!parent) return;
    const validChildren: ETItem[] = [];
    b.childIds.forEach((childId) => {
      const child = byId.get(childId);
      if (!child) return;
      if (child.startDay !== parent.startDay) {
        // eslint-disable-next-line no-console
        console.warn(
          `[ExperienceTimeline] bundle child "${childId}" does not share its ` +
            `parent "${b.parentId}"'s start day — rendering as a standalone item instead of stacking it.`
        );
        return;
      }
      validChildren.push(child);
    });
    if (!validChildren.length) return;

    parentIdsWithChildren.add(parent.id);
    validChildren.forEach((c) => childIdsInBundle.add(c.id));

    const ei = Math.max(
      reachOf(parent),
      ...validChildren.map((c) => reachOf(c))
    );
    units.push({
      si: parent.startDay,
      ei,
      height: 1 + validChildren.length,
      itemIds: [parent.id, ...validChildren.map((c) => c.id)],
      bundle: { parentId: parent.id, childIds: validChildren.map((c) => c.id) }
    });
  });

  laneItems.forEach((it) => {
    if (parentIdsWithChildren.has(it.id) || childIdsInBundle.has(it.id)) {
      return;
    }
    units.push({
      si: it.startDay,
      ei: reachOf(it),
      height: 1,
      itemIds: [it.id]
    });
  });

  units.sort((a, b) => a.si - b.si || b.height - a.height);
  return units;
};

type Cluster = {
  key: string;
  units: AtomicUnit[];
  start: number;
  end: number;
};

const buildClusters = (laneId: string, units: AtomicUnit[]): Cluster[] => {
  const clusters: Cluster[] = [];
  units.forEach((u) => {
    const last = clusters[clusters.length - 1];
    if (last && u.si <= last.end) {
      last.units.push(u);
      last.end = Math.max(last.end, u.ei);
    } else {
      clusters.push({
        key: `${laneId}:${clusters.length}:${u.si}`,
        units: [u],
        start: u.si,
        end: u.ei
      });
    }
  });
  return clusters;
};

export const computeTimelineLayout = (
  input: ComputeLayoutInput
): TimelineLayoutResult => {
  const {
    lanes,
    items,
    bundles = [],
    colWidth,
    rowHeight,
    rowGap,
    collapsedLanes = {},
    expandedClusters = {},
    measureLabelDays = defaultMeasureLabelDays
  } = input;

  const laneLayouts: LaneLayout[] = [];
  const laneById: Record<string, LaneLayout> = {};
  const itemLayouts: Record<string, ItemLayout> = {};
  const badges: ClusterBadge[] = [];
  const bundleSegments: BundleSegment[] = [];
  const hiddenIds = new Set<string>();

  let curY = 0;

  lanes.forEach((lane) => {
    const laneItems = items.filter((it) => it.laneId === lane.id);
    const laneBundles = bundles.filter((b) => {
      const parent = laneItems.find((it) => it.id === b.parentId);
      return !!parent;
    });

    const units = buildLaneUnits(laneItems, laneBundles, measureLabelDays, colWidth);
    const clusters = buildClusters(lane.id, units);

    const collapsible = clusters.some(
      (c) => c.units.reduce((s, u) => s + u.height, 0) > 2
    );
    const laneCollapsed = collapsedLanes[lane.id] === true;

    const finalUnits: AtomicUnit[] = [];
    clusters.forEach((c) => {
      const totalRows = c.units.reduce((s, u) => s + u.height, 0);
      const collapsedHere =
        laneCollapsed && totalRows > 2 && !expandedClusters[c.key];

      if (!collapsedHere) {
        finalUnits.push(...c.units);
        return;
      }

      // Always collapse to exactly one visible item, regardless of whether
      // the earliest/tallest unit in the cluster is a standalone item or a
      // bundle (message + stacked tasks) — bundles are atomic in the packer
      // but must not stay fully visible just because they "won" the sort.
      const top = c.units[0];
      const topAnchorId = top.itemIds[0]; // the item itself, or the bundle's parent
      finalUnits.push({ si: top.si, ei: top.ei, height: 1, itemIds: [topAnchorId] });

      if (top.bundle) {
        top.bundle.childIds.forEach((id) => hiddenIds.add(id));
      }
      c.units.slice(1).forEach((u) => {
        u.itemIds.forEach((id) => hiddenIds.add(id));
      });

      badges.push({
        key: c.key,
        laneId: lane.id,
        anchorItemId: topAnchorId,
        count: totalRows - 1 // every item in the cluster except the one visible anchor
      });
    });

    const trackFree: number[] = [];
    const tightBoundaries = new Set<number>();
    finalUnits.forEach((u) => {
      let r = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let fits = true;
        for (let k = 0; k < u.height; k++) {
          if ((trackFree[r + k] ?? -Infinity) >= u.si) {
            fits = false;
            break;
          }
        }
        if (fits) break;
        r++;
      }
      for (let k = 0; k < u.height; k++) {
        trackFree[r + k] = u.ei;
      }
      u.itemIds.forEach((id, i) => {
        itemLayouts[id] = { itemId: id, row: r + i, y: 0, hidden: false };
        if (i > 0) tightBoundaries.add(r + i);
      });
      if (u.bundle) {
        u.bundle.childIds.forEach((childId) => {
          bundleSegments.push({ bundleParentId: u.bundle!.parentId, childId });
        });
      }
    });

    const rows = Math.max(1, trackFree.length);
    const centers = [rowHeight / 2];
    for (let r = 1; r < rows; r++) {
      const pitch = tightBoundaries.has(r) ? TIGHT_ROW_PITCH : rowHeight + rowGap;
      centers.push(centers[r - 1] + pitch);
    }
    const laneHeight = centers[rows - 1] + rowHeight / 2;

    laneItems.forEach((it) => {
      const il = itemLayouts[it.id];
      if (il && !il.hidden) {
        il.y = curY + centers[il.row];
      } else if (!il) {
        // hidden by a collapsed cluster — never got a row assignment
        itemLayouts[it.id] = { itemId: it.id, row: -1, y: 0, hidden: true };
      }
    });

    const laneLayout: LaneLayout = {
      laneId: lane.id,
      top: curY,
      rows,
      height: laneHeight,
      collapsed: laneCollapsed,
      collapsible
    };
    laneLayouts.push(laneLayout);
    laneById[lane.id] = laneLayout;

    curY += laneHeight + LANE_GAP;
  });

  const totalHeight = laneLayouts.length
    ? curY - LANE_GAP
    : 0;

  return {
    lanes: laneLayouts,
    laneById,
    items: itemLayouts,
    badges,
    bundleSegments,
    hiddenIds,
    totalHeight
  };
};
